import { useEffect, useImperativeHandle, useState } from 'react';

import { ActivityInputValues } from 'components/shared/activityInput/useActivityInput';
import Episode from 'models/Episode';
import LocationEpisode from 'models/LocationEpisode';
import { QuestionKind, QuestionType } from 'models/QuestionTemplate';

import NumericQuestion from './NumericQuestion';
import RangeQuestion from './RangeQuestion';
import StatusQuestion from './StatusQuestion';
import YesNoQuestion from './YesNoQuestion';

export type AnswerType = QuestionType & {
  answer: {
    text: string;
    weight: 'positive' | 'negative' | 'n/a';
  };
};

interface QuestionListProps {
  questions: QuestionType[] | AnswerType[];
  setAllQuestionsAnswered?: (b: boolean) => void;
  setSomeQuestionChanged?: (b: boolean) => void;
  latestStatus?: string;
  locationEpisode?: LocationEpisode;
  episode?: Episode;
  noteValues?: ActivityInputValues;
  setNoteValues?: React.Dispatch<React.SetStateAction<ActivityInputValues>>;
  resetNoteValues?: () => void;
  setIsUploading?: (b: boolean) => void;
  ref?: React.Ref<QuestionListRefType>;
}

export type QuestionListRefType = {
  getAnswers: () => AnswerType[];
  reset: () => void;
};

function QuestionList(props: QuestionListProps) {
  const {
    questions: _questions,
    setAllQuestionsAnswered,
    setSomeQuestionChanged,
    latestStatus = '',
    locationEpisode,
    episode,
    noteValues,
    setNoteValues,
    resetNoteValues,
    setIsUploading,
    ref,
  } = props;

  const questions = _questions.sort((a, b) => a.order - b.order);

  const deriveValues = (qs: (QuestionType | AnswerType)[]) => qs.map((x) => (x as AnswerType).answer?.text ?? '');

  const [values, setValues] = useState<string[]>(() => deriveValues(questions));
  const [prevQuestions, setPrevQuestions] = useState(questions);

  const [inadequateExplanation, setInadequateExplanation] = useState(false);

  if (questions !== prevQuestions) {
    setPrevQuestions(questions);
    setValues(deriveValues(questions));
  }

  useEffect(() => {
    setAllQuestionsAnswered?.(
      values.every(
        (x, i) => !!x && !(i < questions.length && questions[i].kind == QuestionKind.STATUS && inadequateExplanation)
      )
    );
    setSomeQuestionChanged?.(
      values.some((x, i) => i < questions.length && (questions[i] as AnswerType).answer?.text != x)
    );
  }, [inadequateExplanation, questions, setAllQuestionsAnswered, setSomeQuestionChanged, values]);

  const replace = (v: string, i: number) => {
    const newvalues = [...values];
    newvalues[i] = v;
    setValues(newvalues);
  };

  useImperativeHandle(ref, () => ({
    getAnswers: () => {
      return questions.map((question, i) => ({
        ...question,
        answer: {
          text: values[i],
          weight:
            question.kind !== QuestionKind.YES_NO
              ? 'n/a'
              : question.config.positive?.toLowerCase() === values[i].toLowerCase()
                ? 'positive'
                : 'negative',
        },
      })) as AnswerType[];
    },
    reset: () => {
      setValues(questions.map((x) => (x as AnswerType).answer?.text ?? ''));
      resetNoteValues?.();
    },
  }));

  return (
    <>
      {questions.map((question, i) => {
        const common = {
          text: question.text,
          index: i + 1,
          value: values[i],
          setValue: (v: string) => replace(v, i),
        };
        if (question.kind == QuestionKind.NUMERIC) {
          return <NumericQuestion key={i} {...common} />;
        }
        if (question.kind == QuestionKind.YES_NO) {
          return <YesNoQuestion key={i} {...common} />;
        }
        if (question.kind == QuestionKind.RANGE) {
          return <RangeQuestion key={i} max={10} {...common} />;
        }
        if (question.kind == QuestionKind.STATUS) {
          return (
            <StatusQuestion
              key={i}
              {...common}
              latestStatus={latestStatus}
              locationEpisode={locationEpisode!}
              episode={episode!}
              setInadequateExplanation={setInadequateExplanation}
              noteValues={noteValues}
              setNoteValues={setNoteValues}
              setIsUploading={setIsUploading}
            />
          );
        }
        return <></>;
      })}
    </>
  );
}

export default QuestionList;
